import CheckIcon from "@mui/icons-material/Check";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  Button,
  Card,
  DialogTitle,
  InputBaseComponentProps,
  MenuItem,
  SelectChangeEvent,
  Stack,
  styled,
  TextField,
  Typography
} from "@mui/material";
import { SquareIconButton, useToast } from "@qubit/autoparts";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";

import { mixpanelTrack, useMixpanelModalName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { QUANTITY_MAX_VALUE } from "~/lib/shared";
import { selectPutawayReasonCodeOptions } from "~/redux/selectors/inventorySelectors";
import { selectWorkstationId } from "~/redux/selectors/workstationsSelectors";
import { usePatchInventoryNetAdjustmentMutation } from "~/redux/warehouse/inventory.hooks";
import { PutAwayTaskSummaryDto } from "~/types/api";

const DropdownOption = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== "isBorderBottomShown"
})<{ isBorderBottomShown: boolean }>(({ isBorderBottomShown, theme }) => ({
  padding: "10px",
  fontSize: "24px",
  borderBottom: isBorderBottomShown
    ? `1px solid ${theme.palette.gray.light}`
    : "none"
}));

const quantityTextFieldInputProps: InputBaseComponentProps = {
  style: {
    fontSize: 50,
    padding: "35px 10px 35px 18px",
    textAlign: "center"
  }
};

export type Props = {
  closeModal: () => void;
  handleSuccess: () => void;
  putawayTask: PutAwayTaskSummaryDto;
  variant: "under-received" | "over-received";
};

/** Originally copied and modified from the PutawayAdjustModal. This modal has two variants, "Under
 * Received" and "Over Received" which have slightly different behavior. */
export function AdjustQuantityModal(props: Props) {
  const { t } = useTranslation();
  const { closeModal, handleSuccess, putawayTask, variant } = props;

  const { errorToast, successToast } = useToast();

  const isUnderReceive = variant === "under-received";
  const isOverReceive = variant === "over-received";
  const trackedModalName = useMixpanelModalName(
    isUnderReceive ? "Under Receive" : "Over Receive"
  );

  const [adjustmentQuantity, setAdjustmentQuantity] = useState<number>(0);
  const [reasonCode, setReasonCode] = useState<string | null>(null);

  const putawayReasonCodeOptions = useAppSelector(
    selectPutawayReasonCodeOptions
  );
  const workstationId = useAppSelector(selectWorkstationId);

  const [
    patchNetAdjustment,
    {
      isLoading: isAdjustingInventory,
      isSuccess: hasAdjustedInventorySuccessfully
    }
  ] = usePatchInventoryNetAdjustmentMutation();

  const confirmText = `${
    isUnderReceive ? t("remove") : t("add")
  } ${adjustmentQuantity}`;

  const isConfirmButtonEnabled =
    adjustmentQuantity && ((isUnderReceive && reasonCode) || isOverReceive);

  const isDecreaseQuantityButtonDisabled =
    adjustmentQuantity <= 0 ||
    isAdjustingInventory ||
    hasAdjustedInventorySuccessfully;

  const handleReasonCodeChange = (event: SelectChangeEvent<unknown>) => {
    if (typeof event?.target?.value === "string") {
      mixpanelTrack({
        trackedPageName: trackedModalName,
        type: "Select Option",
        label: "Select Reason Code",
        eventProperties: {
          "Reason Code": event.target.value
        }
      });
      setReasonCode(event.target.value);
    }
  };

  const isAdjustmentGreaterThanRemaining = (
    adjustmentQuantityParam: number | null,
    putawayTaskParam: { remaining: { value: number } | null }
  ) => {
    if (
      !putawayTaskParam ||
      !putawayTaskParam.remaining ||
      adjustmentQuantityParam === null
    ) {
      return false;
    }

    return putawayTaskParam.remaining.value < adjustmentQuantityParam;
  };

  const handleSubmit = async () => {
    try {
      const { inventoryId, receivedQuantity, quantity } = putawayTask;
      const qty = receivedQuantity ?? quantity;
      const adjustmentQuantityToSend = isUnderReceive
        ? -adjustmentQuantity
        : adjustmentQuantity;
      const netAdjustment = {
        units: qty.units,
        value: adjustmentQuantityToSend
      };

      // Reason code will always be "Over Received" if the variant is "Over Received"
      const reasonCodeToSend = isUnderReceive ? reasonCode : "Over Received";

      if (!reasonCodeToSend) {
        throw new Error("Reason code was unexpectedly not set");
      }
      if (!inventoryId) {
        throw new Error("Inventory ID was unexpectedly not set");
      }

      await patchNetAdjustment({
        inventoryId,
        netAdjustment,
        reasonCode: reasonCodeToSend,
        workstationId
      }).unwrap();

      mixpanelTrack({
        trackedPageName: trackedModalName,
        type: "Button Click",
        label: `${isUnderReceive ? "Remove" : "Add"} Quantity`,
        eventProperties: {
          Quantity: adjustmentQuantity,
          "Reason Code": reasonCodeToSend,
          Product: putawayTask.product
        }
      });

      successToast(t("inventory adjusted"));
      handleSuccess();
    } catch (err: unknown) {
      errorToast(getMessageFromRtkError(err), {
        trackingLocation: trackedModalName
      });
    }
  };

  const quantityTextField = (
    <>
      <TextField
        sx={{ width: "100%" }}
        label={t("quantity adjustment")}
        type="number"
        variant="outlined"
        value={adjustmentQuantity}
        onChange={(e) => {
          const newValue = e.target.value ? Number(e.target.value) : 0;
          if (newValue > QUANTITY_MAX_VALUE) {
            return;
          }
          if (
            isUnderReceive &&
            putawayTask?.remaining.value !== undefined &&
            newValue <= putawayTask?.remaining.value
          ) {
            setAdjustmentQuantity(newValue);
          } else if (!isUnderReceive) {
            setAdjustmentQuantity(newValue);
          }
        }}
        onBlur={() => {
          if (adjustmentQuantity) {
            mixpanelTrack({
              trackedPageName: trackedModalName,
              type: "Input Change",
              label: "Quantity Input",
              eventProperties: {
                Quantity: adjustmentQuantity
              }
            });
          }
        }}
        inputProps={quantityTextFieldInputProps}
        InputLabelProps={{ shrink: true }}
      />
      {isUnderReceive &&
        putawayTask?.remaining.value !== undefined &&
        adjustmentQuantity > putawayTask?.remaining.value && (
          <Typography style={{ color: "red" }}>
            {t("adjustment warning")}
          </Typography>
        )}
    </>
  );

  const isRemoveButtonDisabled =
    isUnderReceive &&
    putawayTask !== null &&
    isAdjustmentGreaterThanRemaining(adjustmentQuantity, putawayTask);

  const quantityButtons = (
    <Stack direction="column" gap={2} justifyContent="center">
      <SquareIconButton
        aria-label="increase quantity"
        onClick={() => {
          if (adjustmentQuantity >= QUANTITY_MAX_VALUE) {
            return;
          }
          mixpanelTrack({
            trackedPageName: trackedModalName,
            type: "Button Click",
            label: "Increase Quantity",
            eventProperties: {
              Quantity: adjustmentQuantity + 1
            }
          });
          setAdjustmentQuantity(adjustmentQuantity + 1);
        }}
        disabled={
          isAdjustingInventory ||
          hasAdjustedInventorySuccessfully ||
          adjustmentQuantity >= QUANTITY_MAX_VALUE
        }
        size="large"
      >
        <ExpandLessIcon sx={{ fontSize: 27 }} />
      </SquareIconButton>
      <SquareIconButton
        aria-label="decrease quantity"
        onClick={() => {
          mixpanelTrack({
            trackedPageName: trackedModalName,
            type: "Button Click",
            label: "Decrease Quantity",
            eventProperties: {
              Quantity: adjustmentQuantity - 1
            }
          });
          setAdjustmentQuantity(adjustmentQuantity - 1);
        }}
        disabled={isDecreaseQuantityButtonDisabled}
        size="large"
      >
        <ExpandMoreIcon sx={{ fontSize: 27 }} />
      </SquareIconButton>
    </Stack>
  );

  const reasonCodeDropdown = (
    <TextField
      label={t("reason code")}
      type="string"
      variant="outlined"
      value={reasonCode || ""}
      fullWidth
      select
      inputProps={{ style: { fontSize: "1rem" } }}
      InputLabelProps={{ shrink: true }}
      SelectProps={{
        onChange: handleReasonCodeChange,
        displayEmpty: true,
        renderValue: (value): ReactNode => {
          const item = putawayReasonCodeOptions.find(
            ({ value: v }) => v === value
          );
          return item?.label ?? t("select reason code");
        }
      }}
    >
      {putawayReasonCodeOptions.map((option, i, arr) => (
        <DropdownOption
          key={`reason-code-option-${option.value}`}
          value={option.value}
          isBorderBottomShown={i !== arr.length - 1}
        >
          {option.label}
        </DropdownOption>
      ))}
    </TextField>
  );

  const ConfirmButton = (
    <Button
      sx={{ width: "100%", fontWeight: "normal" }}
      startIcon={<CheckIcon />}
      onClick={handleSubmit}
      size="large"
      disabled={
        isAdjustingInventory ||
        isRemoveButtonDisabled ||
        hasAdjustedInventorySuccessfully ||
        !isConfirmButtonEnabled
      }
    >
      {confirmText}
    </Button>
  );

  const CancelButton = (
    <Button
      variant="subtle"
      sx={{ width: "100%", fontWeight: "normal" }}
      size="large"
      startIcon={<HighlightOffIcon />}
      onClick={() => {
        mixpanelTrack({
          trackedPageName: trackedModalName,
          type: "Button Click",
          label: "Cancel"
        });
        closeModal();
      }}
      disabled={isAdjustingInventory || hasAdjustedInventorySuccessfully}
    >
      {t("cancel")}
    </Button>
  );

  return (
    <Card aria-labelledby="adjust-quantity-modal" role="dialog">
      <DialogTitle id="adjust-quantity-modal">
        {isUnderReceive ? t("under received") : t("over received")}
      </DialogTitle>
      <Stack gap={2} alignItems="center" p="20px" flexWrap="nowrap">
        <Stack direction="row" gap={2}>
          {quantityTextField}
          {quantityButtons}
        </Stack>
        {isUnderReceive && reasonCodeDropdown}
        <Stack gap={2} flexDirection="row" width="100%">
          {CancelButton}
          {ConfirmButton}
        </Stack>
      </Stack>
    </Card>
  );
}
